import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const CACHE_KEY = "reviewboost_paywall_preview_v1";

export function clearPaywallPreviewCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

export interface PaywallPreview {
  survivalDays: number;
  deadStockValue: number;
  deadStockItemNames: string[];
  biRecommendationsCount: number;
  ownerName: string;
  businessName: string;
}

function loadCached(): PaywallPreview | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PaywallPreview;
    if (parsed && typeof parsed.survivalDays === "number") return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function saveCached(data: PaywallPreview) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function usePaywallPreview() {
  const { businessId, user, isFounder } = useAuth();
  const [data, setData] = useState<PaywallPreview | null>(loadCached);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreview = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: err } = await supabase.functions.invoke("get-paywall-preview");
      if (err) {
        setError(err.message ?? "Failed to fetch preview");
        const cached = loadCached();
        if (cached) setData(cached);
        return;
      }
      const payload = res as PaywallPreview | { error?: string };
      if (payload && "error" in payload) {
        setError((payload as { error: string }).error);
        const cached = loadCached();
        if (cached) setData(cached);
        return;
      }
      const preview = payload as PaywallPreview;
      if (preview?.survivalDays !== undefined) {
        setData(preview);
        saveCached(preview);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch");
      const cached = loadCached();
      if (cached) setData(cached);
    } finally {
      setLoading(false);
    }
  }, [user, businessId, isFounder]);

  useEffect(() => {
    if (user) fetchPreview();
  }, [user, businessId, isFounder, fetchPreview]);

  return { data, loading, error, refetch: fetchPreview };
}
